import type { ContentVersionBlob } from '@opallabs/api'
import { getVersionCopy } from './deriveTranslatorInputs'
import * as styles from './styles'

type FallbackPreviewCardProps = {
  blob: ContentVersionBlob
  /**
   * Still image of the edit this version came from, when the snapshot pipeline
   * produced one. It shows the content as it looked, which is worth far more
   * than the copy fields alone for asset-led types.
   */
  snapshotUrl?: string | null
  /** Pin layer, mounted over the image. Ignored when there is no image to pin on. */
  overlay?: React.ReactNode
  onImageError?: () => void
}

/**
 * What the drawer shows when the content's service/type can't render through the
 * live Periodic preview: the version's snapshot image, or its copy fields as
 * plain text when there is no snapshot to show. Deliberately has no channel
 * chrome and does not touch the legacy Backbone preview.
 */
export const FallbackPreviewCard: React.VFC<FallbackPreviewCardProps> = ({
  blob,
  snapshotUrl = null,
  overlay = null,
  onImageError,
}) => {
  const { title, body } = getVersionCopy(blob)

  if (snapshotUrl) {
    return (
      // Positioning context for the pin layer, which insets to zero: it has to
      // measure the image, not the column the image sits in.
      <div css={styles.fallbackSnapshotFrame}>
        <img
          css={styles.fallbackSnapshot}
          src={snapshotUrl}
          alt={title ? `${title} preview` : 'Content preview'}
          onError={onImageError}
        />
        {overlay}
      </div>
    )
  }

  return (
    <article css={styles.fallbackCard}>
      {title && <h3 css={styles.fallbackTitle}>{title}</h3>}
      <p css={styles.fallbackBody}>{body}</p>
    </article>
  )
}
